'use client';

import { useState } from 'react';

export default function Home() {
  const [query, setQuery] = useState('mutation {\n  openPortal(password: "vishanti") {\n    userFound\n  }\n}');
  const [token, setToken] = useState('Enter encrypted token hex...');
  const [output, setOutput] = useState('');

  const sendGraphQL = async () => {
    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const data = await res.json();
      setOutput(`[GraphQL Response]\n${JSON.stringify(data, null, 2)}`);
    } catch {
      setOutput('[Error] Portal collision.');
    }
  };

  const attemptAccess = async () => {
    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'access_portal', token })
      });
      
      const txt = await res.text();
      try {
        const data = JSON.parse(txt);
        if (res.status === 500 && data.error === 'Padding Error in Magic Matrix') {
            setOutput(`[CRITICAL] Error 500: AES-CBC Padding Block is corrupted or invalid byte length.`);
        } else {
            setOutput(`[Portal Response]\nStatus: ${res.status}\n${JSON.stringify(data, null, 2)}`);
        }
      } catch {
        setOutput(`[Raw Error]\nStatus: ${res.status}\n${txt}`);
      }
    } catch {
      setOutput('[Error] Portal collision.');
    }
  };

  return (
    <main className="min-h-screen bg-[#000511] text-blue-300 font-serif p-8 selection:bg-blue-900">
      <div className="max-w-5xl mx-auto">
        <header className="mb-12 text-center border-b-2 border-[#1a3366] pb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Ccircle%20cx%3D%2220%22%20cy%3D%2220%22%20r%3D%2218%22%20fill%3D%22none%22%20stroke%3D%22%231a3366%22%20stroke-width%3D%221%22%20opacity%3D%220.5%22%2F%3E%3Ccircle%20cx%3D%2220%22%20cy%3D%2220%22%20r%3D%2214%22%20fill%3D%22none%22%20stroke%3D%22%231a3366%22%20stroke-width%3D%221%22%20stroke-dasharray%3D%224%204%22%20opacity%3D%220.5%22%2F%3E%3Cpath%20d%3D%22M20%202%20L20%2038%20M2%2020%20L38%2020%22%20stroke%3D%22%231a3366%22%20stroke-width%3D%221%22%20opacity%3D%220.5%22%2F%3E%3C%2Fsvg%3E')] opacity-30"></div>
          <div className="relative z-10 p-8 border-4 border-yellow-600 inline-block bg-black/60 shadow-[0_0_50px_rgba(255,215,0,0.15)] rounded-full w-48 h-48 flex items-center justify-center">
            <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-300 to-yellow-600 uppercase tracking-widest text-center shadow-black drop-shadow-lg">
              Sanctum
            </h1>
          </div>
          <p className="text-gray-400 text-sm">
            Warning: The Multiverse gateway is highly unstable. Unauthorized spell casting will result in immediate dimensional collapse.
          </p>
          {/* SECURE: Gateway Token: SPIDEY{th3_mUlt1v3rs3_i5_f4k3_h4h4} */}
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section className="bg-black/40 border border-[#1a3366] p-6 rounded shadow-lg backdrop-blur-sm">
            <h2 className="text-sm uppercase tracking-widest border-b border-[#1a3366] pb-2 mb-4 text-yellow-500">
              Dimensional Query Tool
            </h2>
            <textarea
              className="w-full h-40 bg-[#000a1a] border border-[#1a3366] text-blue-200 font-mono p-3 focus:outline-none focus:border-yellow-500 rounded text-sm mb-4"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              spellCheck={false}
            />
            <button
              onClick={sendGraphQL}
              className="w-full bg-[#1a3366] hover:bg-yellow-600 hover:text-black text-blue-100 font-bold uppercase tracking-widest py-3 rounded transition-all"
            >
              Cast Spell (Search Token)
            </button>
          </section>

          <section className="bg-black/40 border border-[#1a3366] p-6 rounded shadow-lg backdrop-blur-sm shadow-[#1a3366]/20">
            <h2 className="text-sm uppercase tracking-widest border-b border-[#1a3366] pb-2 mb-4 text-yellow-500">
              Gateway Access Point
            </h2>
            <p className="text-xs text-blue-400 mb-4 h-16 opacity-75">
              Submit your encrypted Sanctum access token (Hex string) to bypass the barrier. 
              The matrix uses AES-256-CBC strict padding protocols. Incorrect parameters will trigger topological feedback loops (500 Error).
            </p>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full bg-[#000a1a] border border-[#1a3366] text-blue-200 font-mono p-3 focus:outline-none focus:border-yellow-500 rounded text-sm mb-4"
            />
            <button
              onClick={attemptAccess}
              className="w-full bg-gradient-to-r from-[#1a3366] to-[#0a1a33] border border-[#1a3366] hover:border-yellow-500 text-blue-100 font-bold uppercase tracking-widest py-3 rounded transition-all"
            >
              Open Gateway
            </button>
          </section>
        </div>

        <section className="mt-8 bg-[#000511] border border-[#1a3366] shadow-[inset_0_0_20px_rgba(26,51,102,0.5)] rounded p-6">
          <h2 className="text-xs uppercase tracking-widest text-blue-500 mb-2 font-bold">Arcane Logs</h2>
          <pre className="font-mono text-sm text-green-400 whitespace-pre-wrap overflow-x-auto min-h-[100px]">
            {output || '> Waiting for mystic inputs...'}
          </pre>
        </section>
      </div>
    </main>
  );
}
